import {lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {
  useActiveChangeGroup,
  useCommentsPreference,
  useDiffEntries,
  useDiffEntriesDigestsWithFilter,
  useDiffSummaries,
  useDiffSummaryCount,
  useGetCollapsedDiffStatus,
  useGetProgressiveDiffEntryByPathDigest,
  useLineSpacingPreference,
  useProgressiveDiffActions,
  useSplitPreference,
  useThreadSizeInfoMap,
} from '../stores/selectors'
import {ScrollToSelectedDiff} from './ScrollToSelectedDiff'

import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {defaultRangeExtractor, type Range, useWindowVirtualizer} from '@tanstack/react-virtual'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {VirtualizedDiffRow} from './PullRequestVirtualizedDiffRow'
import virtualizedStyles from './PullRequestVirtualizedDiffsList.module.css'
import {DIFF_LIST_GAP, estimateDiffSize} from '../helpers/estimate-diff-size'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BASE_DIFF_MARGIN_TOP_PADDING, STICKY_HEADER_HEIGHT} from '../helpers/constants'

const PullRequestDiffsAnimation = lazy(async () => {
  const module = await import('./PullRequestDiffsAnimation')
  return {default: module.PullRequestDiffsAnimation}
})

export interface PullRequestVirtualizedDiffsListProps {
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export function PullRequestVirtualizedDiffsList({
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestVirtualizedDiffsListProps) {
  'use no memo'

  const commentPreference = useCommentsPreference()
  const lineSpacingPreference = useLineSpacingPreference()
  const splitPreference = useSplitPreference()
  const diffsParentRef = useRef<HTMLDivElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const diffEntryDigests = useDiffEntriesDigestsWithFilter()
  const getProgressiveDiffEntry = useGetProgressiveDiffEntryByPathDigest()
  const activeChangeGroup = useActiveChangeGroup()
  const {startQueue, loadManyPriority} = useProgressiveDiffActions()

  const diffSummariesCount = useDiffSummaryCount()
  const allDiffSummaries = useDiffSummaries()
  const diffEntries = useDiffEntries()
  const threadSizeInfoMap = useThreadSizeInfoMap()

  const totalVisibleEntries = diffEntryDigests.length
  const shouldShowWhimsy = totalVisibleEntries >= 15
  const getCollapsedDiffStatus = useGetCollapsedDiffStatus()

  // Feature flags - declared before useCallback that depends on them
  const isDebugVirtualizationEnabled = isFeatureEnabled('pull_request_files_debug_virtualization')
  // Enables more accurate height estimation using thread reply counts, collapsed state, and split view mode
  const isAccurateSizeEstimatesEnabled = isFeatureEnabled('pull_request_files_accurate_size_estimates')
  // Enables preloading diffs beyond the visible virtualized range for smoother scrolling
  const isPreloadDiffsEnabled = isFeatureEnabled('pull_request_virtualization_preload_diffs')

  useEffect(() => {
    startQueue()
  }, [startQueue])

  /**
   * Estimate size in pixels of each diff entry.
   */
  const estimateVirtualDiffSize = useCallback(
    (index: number) => {
      const digest = diffEntryDigests[index]
      if (!digest) return 0
      const progressiveDiffEntry = getProgressiveDiffEntry(digest)
      if (!progressiveDiffEntry) return 0

      const diffSummary = allDiffSummaries[progressiveDiffEntry.path ?? '']
      const isCollapsed = getCollapsedDiffStatus(progressiveDiffEntry.path ?? '')

      return estimateDiffSize({
        diffSummary,
        commentPreference,
        lineSpacingPreference,
        isCollapsed,
        // Only use enhanced estimation when feature flag is enabled
        threadSizeInfoMap: isAccurateSizeEstimatesEnabled ? threadSizeInfoMap : undefined,
        isSplitView: isAccurateSizeEstimatesEnabled && splitPreference === 'split',
      })
    },
    [
      getProgressiveDiffEntry,
      diffEntryDigests,
      allDiffSummaries,
      getCollapsedDiffStatus,
      commentPreference,
      lineSpacingPreference,
      isAccurateSizeEstimatesEnabled,
      threadSizeInfoMap,
      splitPreference,
    ],
  )

  // Key is a diffDigest
  const getItemKey = useCallback((index: number) => diffEntryDigests[index] ?? index, [diffEntryDigests])

  useEffect(() => {
    if (diffsParentRef.current) {
      setScrollMargin(diffsParentRef.current.offsetTop)
    }
  }, [])

  const defaultOverscan = 5

  const overscan = useMemo(() => {
    if (diffEntries && Object.keys(diffEntries).length === diffSummariesCount) {
      return defaultOverscan
    }
    return 0
  }, [diffEntries, diffSummariesCount])

  const pathDigestToIndex = useMemo(() => {
    const indexMap = new Map<string, number>()

    for (let index = 0; index < diffEntryDigests.length; index++) {
      const pathDigest = diffEntryDigests[index]
      if (!pathDigest) continue
      indexMap.set(pathDigest, index)
    }

    return indexMap
  }, [diffEntryDigests])

  /**
   * Custom range extractor that ensures focused and selected diffs stay in the virtual window.
   * This prevents focused elements from being unmounted during scrolling, which is critical
   * for keyboard navigation and accessibility.
   */
  const rangeExtractor = useCallback(
    (range: Range) => {
      // Find the diff that currently has focus within it
      const focusedPathDigest = document
        .querySelector('[data-path-digest]:focus-within')
        ?.getAttribute('data-path-digest')

      const focusedDiffIndex = focusedPathDigest ? (pathDigestToIndex.get(focusedPathDigest) ?? -1) : -1

      if (focusedDiffIndex < 0) {
        return defaultRangeExtractor(range)
      }

      // Extend the default range to include the focused index
      const nextRange = new Set(defaultRangeExtractor(range))
      nextRange.add(focusedDiffIndex)

      return Array.from(nextRange).sort((a, b) => a - b)
    },
    [pathDigestToIndex],
  )

  const virtualizer = useWindowVirtualizer({
    count: diffEntryDigests.length,
    estimateSize: estimateVirtualDiffSize,
    getItemKey,
    overscan,
    // We use the sticky header height and base diff margin top padding
    // to ensure that we scroll to the diff header instead of a random line in a diff entry.
    scrollPaddingStart: BASE_DIFF_MARGIN_TOP_PADDING + STICKY_HEADER_HEIGHT,
    scrollMargin,
    gap: DIFF_LIST_GAP,
    rangeExtractor,
    debug: isDebugVirtualizationEnabled,
    // Note: We intentionally do NOT use useAnimationFrameWithResizeObserver.
    // The tanstack/virtual docs recommend against it as ResizeObserver already
    // runs at an optimal time (after layout, before paint). Enabling it adds
    // ~16ms delay and provides no batching benefit.
    onChange(instance) {
      instance.shouldAdjustScrollPositionOnItemSizeChange = (_item, _delta, _inst) => {
        // Don't adjust scroll position on item size changes
        // This prevents jarring viewport jumps when items resize
        return false
      }
    },
  })

  const virtualItems = virtualizer.getVirtualItems()

  // Load data for visible items AND optionally preload the next/previous 5 items.
  // This only fetches network data — it does NOT add items to the virtualizer's
  // rendered range, so it won't affect scroll position or layout.
  const preloadCount = 5
  useEffect(() => {
    if (isPreloadDiffsEnabled) {
      if (virtualItems.length === 0) return

      const firstIndex = virtualItems[0]?.index ?? 0
      const lastIndex = virtualItems[virtualItems.length - 1]?.index ?? 0

      const start = Math.max(0, firstIndex - preloadCount)
      const end = Math.min(diffEntryDigests.length, lastIndex + preloadCount + 1)

      const digests = diffEntryDigests.slice(start, end).filter(digest => digest !== undefined)
      loadManyPriority(digests)
    } else {
      // Without preloading, only load the currently visible/virtualized items
      loadManyPriority(virtualItems.map(item => item.key as string))
    }
  }, [isPreloadDiffsEnabled, loadManyPriority, virtualItems, diffEntryDigests])

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (diffEntryDigests.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc data-testid="virtualized-diffs-list" className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      <div
        className={virtualizedStyles['virtualized-diff-row']}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualItems.map(virtualRow => {
          const pathDigest = diffEntryDigests[virtualRow.index]
          if (!pathDigest) return null
          const diffEntry = getProgressiveDiffEntry(pathDigest)
          return (
            <VirtualizedDiffRow
              key={virtualRow.key}
              virtualRow={virtualRow}
              virtualizedDiffEntry={diffEntry}
              diffEntry={diffEntries[diffEntry?.path ?? '']}
              diffSummary={allDiffSummaries[diffEntry?.path ?? '']}
              scrollMargin={virtualizer.options.scrollMargin}
              measureElement={virtualizer.measureElement}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              repository={repository}
              virtualWindowHeight={virtualizer.scrollRect?.height}
            />
          )
        })}
      </div>
      {shouldShowWhimsy && (
        <ErrorBoundary boundaryName="PullRequestVirtualizedDiffsListAnimation" fallback={null}>
          <Suspense fallback={null}>
            <PullRequestDiffsAnimation />
          </Suspense>
        </ErrorBoundary>
      )}

      <ScrollToSelectedDiff
        virtualizer={virtualizer}
        virtualItems={virtualItems}
        pathDigestToIndex={pathDigestToIndex}
        diffEntryDigests={diffEntryDigests}
        loadManyPriority={loadManyPriority}
      />

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}
